import { useQuery } from '@tanstack/react-query';
import { Flex } from 'antd';
import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { ConditionView, Grid, Image, Loader, StepControlPanel, Typography } from '@/shared/ui';
import { ClickPlace } from '@/shared/ui/ClickPlace/ClickPlace';

import { LinkAsButton } from '@/components/Button';

import { ExtractFormTypeSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { ROUTES } from '@/router/baseRoutes';

import AuthContext from '@/contexts/Auth/AuthContext';

import { queryKeys } from '@/api/queryKeys';
import { DataResponse, FormDataType } from '@/api/types';

import api from '@/api';

import { ExtractFormType, ExtractFormTypeLabelMapping, SERVICE_CODE } from '../../lib/constants';

export interface ExtractFormTypeStepProps {
  selectedExtractFormType: ExtractFormType;
  paymentStepToOpen?: Nullable<number>;
  onExtractFormTypeSelect: (extractFormType: ExtractFormType) => void;
  onPaymentStepOpened?: () => void;
  onDraftCreate: (data: { stateForm: FormDataType }, specificStep: (step: number) => void) => Promise<Nullable<string>>;
}

export const ExtractFormTypeStep: React.FC<StepProps<ExtractFormTypeStepProps>> = ({
  selectedExtractFormType,
  paymentStepToOpen,
  onExtractFormTypeSelect,
  onPaymentStepOpened,
  onDraftCreate,
  specificStep,
}) => {
  const { setValue } = useFormContext<ExtractFormTypeSchema>();
  const { storedLinks } = useContext(AuthContext);

  const isElectronicExtractDisabled = !storedLinks?.integration?.iac?.service?.ceaa05?.flow?.electronic;
  const isPaperExtractDisabled = !storedLinks?.integration?.iac?.service?.ceaa05?.flow?.paper;

  const handleExtractFormTypeSelect = (value: ExtractFormType): void => {
    onExtractFormTypeSelect(value);
  };

  const {
    data: responseData,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.services.hasActiveRequest(SERVICE_CODE),
    queryFn: (): Promise<DataResponse<Record<'answer', boolean>>> => api.services.hasActiveRequest(SERVICE_CODE),
    retry: false,
  });

  useEffect(() => {
    setValue('isElectronicExtractDisabled', isElectronicExtractDisabled);
    setValue('isPaperExtractDisabled', isPaperExtractDisabled);
  }, []);

  useEffect(() => {
    if (!paymentStepToOpen) return;

    if (selectedExtractFormType !== ExtractFormType.UNSET) {
      setValue('extractFormType', selectedExtractFormType);
    }

    specificStep(paymentStepToOpen);
    onPaymentStepOpened?.();
  }, [paymentStepToOpen, selectedExtractFormType, setValue, specificStep, onPaymentStepOpened]);

  useEffect(() => {
    if (paymentStepToOpen || selectedExtractFormType === ExtractFormType.UNSET) {
      return;
    }

    setValue('extractFormType', selectedExtractFormType);

    onDraftCreate(
      {
        stateForm: {
          extractFormType: selectedExtractFormType,
        },
      },
      specificStep,
    );
  }, [paymentStepToOpen, selectedExtractFormType, setValue, onDraftCreate, specificStep]);

  return (
    <ConditionView if={!isLoading} else={<Loader centered />}>
      <ConditionView
        if={!!error || !!responseData?.data?.answer}
        else={
          <FormBlock title="Виберіть форму отримання витягу">
            <ClickPlace<ExtractFormType>
              className="iac-click-place"
              data={[
                {
                  label: ExtractFormTypeLabelMapping[ExtractFormType.ELECTRONIC]!,
                  icon: 'smartphone',
                  value: ExtractFormType.ELECTRONIC,
                  disabled: isElectronicExtractDisabled,
                },
                {
                  label: (
                    <>
                      Хочу отримати паперову
                      <br /> форму витягу
                      <br /> (в т.ч. з апостилем)
                    </>
                  ),
                  icon: 'paper_doc',
                  value: ExtractFormType.PAPER,
                  disabled: isPaperExtractDisabled,
                },
              ]}
              onClick={handleExtractFormTypeSelect}
            />
          </FormBlock>
        }
      >
        <FormBlock title="Виберіть форму отримання витягу">
          <Flex justify="space-between">
            <div className="duplicated_application_error">
              <div>
                <Image src="/images/criss-cross_circle_filled_red.svg" alt="Criss Cross" width={40} height={40} />
              </div>
              {error ? (
                <>
                  <Typography variant="H3">Помилка</Typography>
                  <Typography variant="TEXT16">Щось пішло не так, спробуйте, будь ласка, пізніше.</Typography>
                </>
              ) : (
                <>
                  <Typography variant="H3">Помилка</Typography>
                  <Typography variant="TEXT16">
                    У вас вже є один запит в процесі опрацювання. Ви не можете подати запит повторно, поки не буде
                    опрацьовано попередній запит на отримання витягу.
                  </Typography>
                  <Typography variant="TEXT16">
                    Щоб дізнатися статус існуючого запиту перейдіть в розділ “Мої запити”
                  </Typography>
                </>
              )}
            </div>
          </Flex>
        </FormBlock>
        <StepControlPanel>
          <Flex justify="space-between" gap={8} align="center">
            <LinkAsButton to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS} isFormButton bgColor="sky-blue">
              Мої запити
            </LinkAsButton>
            <LinkAsButton to={ROUTES.HOME} isFormButton position="right-side">
              На головну
            </LinkAsButton>
          </Flex>
        </StepControlPanel>
      </ConditionView>
    </ConditionView>
  );
};

export const ExtractFormTypePreview: React.FC = () => {
  const { getValues } = useFormContext<ExtractFormTypeSchema>();
  const extractFormType: ExtractFormType = getValues().extractFormType as ExtractFormType;

  return (
    <Grid
      title="Форма отримання витягу"
      data={[['Виберіть форму отримання витягу', ExtractFormTypeLabelMapping[extractFormType]]]}
    />
  );
};
